import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, List, Map as MapIcon, Navigation, ChevronDown, ChevronUp, Star, Users, X, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import ResponsiveHeader from '../components/ResponsiveHeader';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SmartLocationSearch from '../components/SmartLocationSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RADIUS_OPTIONS = [
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 200, label: '200 km' },
];

const DEFAULT_RADIUS = 50;

// Bottom sheet heights (in vh)
const SHEET_COLLAPSED = 15;  // Show just header
const SHEET_HALF = 45;       // Half screen
const SHEET_FULL = 85;       // Almost full screen

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const infoWindowRef = useRef(null);
  const boundsChangeTimeoutRef = useRef(null);
  const mapBrowseModeRef = useRef(false);
  const bottomSheetRef = useRef(null);
  const trainerListRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const isSelectingTrainerRef = useRef(false); // Prevent idle event during trainer selection
  const mapViewInitializedRef = useRef(false); // Track if map view has been fully initialized
  const isZoomingRef = useRef(false); // Prevent idle event during zoom animation
  const trainersRef = useRef([]); // Keep trainers in ref for event listener access
  
  // Core state
  const [hasSearched, setHasSearched] = useState(false);
  const [searchedAddress, setSearchedAddress] = useState(null);
  const [centerLat, setCenterLat] = useState(43.6532);
  const [centerLng, setCenterLng] = useState(-79.3832);
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [hoveredTrainerId, setHoveredTrainerId] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [showRadiusDropdown, setShowRadiusDropdown] = useState(false);
  const [mapBrowseMode, setMapBrowseMode] = useState(false);
  
  // Mobile bottom sheet state
  const [sheetHeight, setSheetHeight] = useState(SHEET_HALF);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(SHEET_HALF);
  const [showSearchAreaButton, setShowSearchAreaButton] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Fetch trainers on initial load (if no search params) - with distance filter
  useEffect(() => {
    const urlLat = parseFloat(searchParams.get('lat'));
    const urlLng = parseFloat(searchParams.get('lng'));
    
    // If no location params, fetch trainers near default location
    if (!urlLat && !urlLng && !hasSearched) {
      // Use default Toronto coordinates with default radius
      searchNearby(centerLat, centerLng, radiusKm);
    }
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    mapBrowseModeRef.current = mapBrowseMode;
  }, [mapBrowseMode]);

  // Keep trainers ref in sync with state for event listeners
  useEffect(() => {
    trainersRef.current = trainers;
  }, [trainers]);

  // Handle URL params on initial load
  useEffect(() => {
    const urlLat = parseFloat(searchParams.get('lat'));
    const urlLng = parseFloat(searchParams.get('lng'));
    const urlCity = searchParams.get('city');
    const urlProvince = searchParams.get('province');
    const urlRadius = parseInt(searchParams.get('radius')) || DEFAULT_RADIUS;
    
    if (urlLat && urlLng) {
      setCenterLat(urlLat);
      setCenterLng(urlLng);
      setRadiusKm(urlRadius);
      
      // Build formatted address from city and province
      let addressParts = [];
      if (urlCity) addressParts.push(urlCity);
      if (urlProvince) addressParts.push(urlProvince);
      addressParts.push('Canada');
      const formattedAddress = addressParts.join(', ');
      
      setSearchedAddress({
        formatted_address: formattedAddress,
        city: urlCity || '',
        province: urlProvince || '',
        coordinates: { lat: urlLat, lng: urlLng }
      });
      
      searchNearby(urlLat, urlLng, urlRadius);
      
      if (isMobile) {
        setViewMode('map');
      }
    }
  }, [isMobile]);

  // Load Google Maps Script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    if (window.google?.maps) {
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps) {
          setIsScriptLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize Map
  const initializeMap = useCallback(() => {
    if (!isScriptLoaded || !mapContainerRef.current) {
      console.log('Map init blocked: script loaded:', isScriptLoaded, 'container:', !!mapContainerRef.current);
      return;
    }
    
    // If map exists but container changed (mobile/desktop switch), destroy old map
    if (mapRef.current && mapInitializedRef.current) {
      // Check if the current map is attached to our container
      const mapDiv = mapRef.current.getDiv();
      if (mapDiv !== mapContainerRef.current) {
        console.log('Map container changed, reinitializing...');
        mapRef.current = null;
        mapInitializedRef.current = false;
      }
    }
    
    if (!mapRef.current) {
      console.log('Creating new map instance');
      const map = new window.google.maps.Map(mapContainerRef.current, {
        center: { lat: centerLat, lng: centerLng },
        zoom: getZoomForRadius(radiusKm),
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: !isMobile
      });
      
      mapRef.current = map;
      mapInitializedRef.current = true;
      infoWindowRef.current = new window.google.maps.InfoWindow();

      // Auto-update on map move (browse mode)
      map.addListener('idle', () => {
        // Skip if selecting trainer (pan animation in progress)
        if (isSelectingTrainerRef.current) {
          return;
        }
        
        // Skip if zooming - we only want to update markers, not search
        if (isZoomingRef.current) {
          isZoomingRef.current = false;
          return;
        }
        
        if (mapBrowseModeRef.current) {
          if (boundsChangeTimeoutRef.current) {
            clearTimeout(boundsChangeTimeoutRef.current);
          }
          boundsChangeTimeoutRef.current = setTimeout(() => {
            // Double-check browse mode is still active
            if (mapBrowseModeRef.current) {
              searchInBounds();
            }
          }, 500); // Reduced from 800ms for faster response
        }
      });

      // Show "Search this area" button when map is dragged
      map.addListener('dragend', () => {
        if (!mapBrowseModeRef.current && hasSearched) {
          setShowSearchAreaButton(true);
        }
      });
      
      // Handle zoom changes - just prevent idle event from searching during zoom
      map.addListener('zoom_changed', () => {
        isZoomingRef.current = true;
        // Reset flag after zoom animation completes
        setTimeout(() => {
          isZoomingRef.current = false;
        }, 500);
      });
    }
    
    if (mapRef.current) {
      setTimeout(() => {
        window.google.maps.event.trigger(mapRef.current, 'resize');
        // Only set center/zoom on first initialization, not on re-renders
        if (!mapViewInitializedRef.current) {
          mapRef.current.setCenter({ lat: centerLat, lng: centerLng });
          mapRef.current.setZoom(getZoomForRadius(radiusKm));
          mapViewInitializedRef.current = true;
          if (!mapBrowseMode) {
            updateCircle();
          }
        }
        // Always update markers when map is ready
        updateMarkers();
      }, 100);
    }
  }, [isScriptLoaded, isMobile]); // Reduced dependencies - only re-run when script loads or mobile changes

  // Initialize map when switching to map view
  useEffect(() => {
    if (viewMode === 'map') {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        initializeMap();
        // After map is initialized, update with current data
        if (mapRef.current && !mapBrowseMode) {
          console.log(`🗺️ Map view activated: updating to center=(${centerLat}, ${centerLng}), radius=${radiusKm}km`);
          mapRef.current.setCenter({ lat: centerLat, lng: centerLng });
          mapRef.current.setZoom(getZoomForRadius(radiusKm));
          updateCircle(true);
          updateMarkers();
        }
      }, 150);
      return () => clearTimeout(timer);
    } else {
      // Reset the flag when leaving map view
      mapViewInitializedRef.current = false;
    }
  }, [viewMode, initializeMap, centerLat, centerLng, radiusKm, mapBrowseMode]);

  // Update markers when trainers change or view mode changes to map
  useEffect(() => {
    if (viewMode === 'map' && isScriptLoaded) {
      // Delay to ensure map is fully initialized
      const timer = setTimeout(() => {
        if (mapRef.current) {
          console.log('🔄 Trainers/view changed, updating markers...');
          updateMarkers();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [trainers, viewMode, isScriptLoaded]); // updateMarkers is stable via useCallback

  // Update map when center/radius changes (only on explicit search, not on trainer select)
  useEffect(() => {
    // Skip if currently selecting a trainer
    if (isSelectingTrainerRef.current) return;
    
    // Update map regardless of viewMode - map might be visible in split view or will be visible when switched
    if (mapRef.current && !mapBrowseMode) {
      console.log(`🗺️ Updating map: center=(${centerLat}, ${centerLng}), radius=${radiusKm}km`);
      mapRef.current.setCenter({ lat: centerLat, lng: centerLng });
      mapRef.current.setZoom(getZoomForRadius(radiusKm));
      updateCircle(true); // Force fit bounds
      updateMarkers();
    }
  }, [centerLat, centerLng, radiusKm, mapBrowseMode]);

  // Highlight marker on hover/select - update icon color and animation
  useEffect(() => {
    if (!markersRef.current.length || !window.google) return;
    
    markersRef.current.forEach(({ marker, trainerId }) => {
      if (trainerId === 'center') return; // Skip center marker
      
      const isSelected = selectedTrainer?.id === trainerId;
      const isHovered = trainerId === hoveredTrainerId;
      
      // Update marker icon color based on selection
      const fillColor = isSelected ? '#1d4ed8' : '#dc2626';
      marker.setIcon({
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="45" viewBox="0 0 36 45">
            <path d="M18 0C8.059 0 0 8.059 0 18c0 13.068 18 27 18 27s18-13.932 18-27C36 8.059 27.941 0 18 0z" fill="${fillColor}"/>
            <circle cx="18" cy="16" r="8" fill="#ffffff"/>
            <text x="18" y="20" font-size="10" text-anchor="middle" fill="${fillColor}">🏋️</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(36, 45),
        anchor: new window.google.maps.Point(18, 45)
      });
      
      // Set animation and z-index
      if (isSelected || isHovered) {
        marker.setAnimation(window.google.maps.Animation.BOUNCE);
        marker.setZIndex(1000);
        // Stop bouncing after a short time
        setTimeout(() => {
          if (marker.getAnimation()) {
            marker.setAnimation(null);
          }
        }, 1500);
      } else {
        marker.setAnimation(null);
        marker.setZIndex(1);
      }
    });
  }, [hoveredTrainerId, selectedTrainer]);

  // Scroll to selected trainer in list
  useEffect(() => {
    if (selectedTrainer && trainerListRef.current) {
      const element = document.getElementById(`trainer-${selectedTrainer.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedTrainer]);

  const getZoomForRadius = (radius) => {
    if (radius <= 25) return 11;
    if (radius <= 50) return 10;
    if (radius <= 100) return 9;
    if (radius <= 200) return 8;
    return 6;
  };

  const updateCircle = useCallback((shouldFitBounds = false) => {
    if (!mapRef.current) return;

    if (circleRef.current) {
      circleRef.current.setMap(null);
    }

    if (mapBrowseMode) return;

    circleRef.current = new window.google.maps.Circle({
      map: mapRef.current,
      center: { lat: centerLat, lng: centerLng },
      radius: radiusKm * 1000,
      fillColor: '#ef4444',
      fillOpacity: 0.1,
      strokeColor: '#ef4444',
      strokeOpacity: 0.5,
      strokeWeight: 2
    });

    // Only fit bounds when explicitly requested (initial search)
    if (shouldFitBounds) {
      mapRef.current.fitBounds(circleRef.current.getBounds());
    }
  }, [centerLat, centerLng, radiusKm, mapBrowseMode]);

  const updateMarkers = useCallback(() => {
    if (!mapRef.current) {
      console.log('❌ updateMarkers: No map ref');
      return;
    }
    
    if (!window.google || !window.google.maps) {
      console.log('❌ updateMarkers: Google Maps not loaded');
      return;
    }

    // Use ref for trainers to get latest value in event listeners
    const currentTrainers = trainersRef.current.length > 0 ? trainersRef.current : trainers;

    console.log(`🗺️ updateMarkers called with ${currentTrainers.length} trainers`);
    
    // Clear existing markers
    markersRef.current.forEach(({ marker }) => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];

    // Add center marker if searching by address
    if (searchedAddress && !mapBrowseMode) {
      const centerMarker = new window.google.maps.Marker({
        position: { lat: centerLat, lng: centerLng },
        map: mapRef.current,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        },
        title: 'Search Location',
        zIndex: 999
      });
      markersRef.current.push({ marker: centerMarker, trainerId: 'center' });
      console.log('✅ Center marker added');
    }

    // Group trainers by location (within ~50 meters = 0.0005 degrees)
    const PROXIMITY_THRESHOLD = 0.0005;
    const locationGroups = [];
    
    currentTrainers.forEach((trainer) => {
      if (!trainer.location?.coordinates) return;
      
      const coords = trainer.location.coordinates;
      let lat, lng;
      
      if (Array.isArray(coords)) {
        lng = coords[0];
        lat = coords[1];
      } else if (typeof coords === 'object') {
        lat = coords.lat || trainer.location.lat;
        lng = coords.lng || trainer.location.lng;
      } else {
        return;
      }
      
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;
      
      lat = parseFloat(lat);
      lng = parseFloat(lng);
      
      // Find existing group within proximity
      let foundGroup = locationGroups.find(group => 
        Math.abs(group.lat - lat) < PROXIMITY_THRESHOLD && 
        Math.abs(group.lng - lng) < PROXIMITY_THRESHOLD
      );
      
      if (foundGroup) {
        foundGroup.trainers.push({ trainer, lat, lng });
      } else {
        locationGroups.push({ lat, lng, trainers: [{ trainer, lat, lng }] });
      }
    });

    // Create markers based on zoom level
    let markersCreated = 0;
    
    // Single trainer marker icon SVG
    const singleMarkerSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="45" viewBox="0 0 36 45">
        <path d="M18 0C8.059 0 0 8.059 0 18c0 13.068 18 27 18 27s18-13.932 18-27C36 8.059 27.941 0 18 0z" fill="#dc2626"/>
        <circle cx="18" cy="16" r="8" fill="#ffffff"/>
        <text x="18" y="20" font-size="10" text-anchor="middle" fill="#dc2626">🏋️</text>
      </svg>
    `;
    
    locationGroups.forEach((group) => {
      const { lat, lng, trainers: groupTrainers } = group;
      const count = groupTrainers.length;
      
      if (count === 1) {
        // Single trainer - show individual marker
        const { trainer } = groupTrainers[0];
        const markerLat = groupTrainers[0].lat;
        const markerLng = groupTrainers[0].lng;
          
        const marker = new window.google.maps.Marker({
          position: { lat: markerLat, lng: markerLng },
          map: mapRef.current,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(singleMarkerSvg),
            scaledSize: new window.google.maps.Size(36, 45),
            anchor: new window.google.maps.Point(18, 45)
          },
          title: trainer.name,
          zIndex: 100
        });

        marker.addListener('click', () => {
          setSelectedTrainer(trainer);
          showTrainerInfoWindow(trainer, marker);
        });

        markersRef.current.push({ marker, trainerId: trainer.id });
        markersCreated++;
      } else {
        // Show grouped marker with badge (zoomed out)
        const badgeMarkerSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 44 52">
            <path d="M18 0C8.059 0 0 8.059 0 18c0 13.068 18 27 18 27s18-13.932 18-27C36 8.059 27.941 0 18 0z" fill="#dc2626"/>
            <circle cx="18" cy="16" r="8" fill="#ffffff"/>
            <text x="18" y="20" font-size="10" text-anchor="middle" fill="#dc2626">🏋️</text>
            <circle cx="32" cy="10" r="11" fill="#1d4ed8" stroke="#ffffff" stroke-width="2"/>
            <text x="32" y="15" font-size="12" font-weight="bold" text-anchor="middle" fill="#ffffff">${count}</text>
          </svg>
        `;
        
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: mapRef.current,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(badgeMarkerSvg),
            scaledSize: new window.google.maps.Size(44, 52),
            anchor: new window.google.maps.Point(18, 52)
          },
          title: `${count} trainers at this location`,
          zIndex: 1000 + count
        });

        marker.addListener('click', () => {
          // Show list popup with all trainers at this location
          const trainerList = groupTrainers.map(item => item.trainer);
          showMultiTrainerInfoWindow(trainerList, marker, lat, lng);
        });

        // Store marker reference for all trainers in group
        groupTrainers.forEach(item => {
          markersRef.current.push({ marker, trainerId: item.trainer.id, group: groupTrainers });
        });
        markersCreated++;
      }
    });
    
    console.log(`✅ Created ${markersCreated} markers for ${currentTrainers.length} trainers`);
  }, [trainers, searchedAddress, centerLat, centerLng, mapBrowseMode]);

  // Helper function to show single trainer info window
  const showTrainerInfoWindow = (trainer, marker) => {
    if (!infoWindowRef.current || !mapRef.current) return;
    
    const hasPhoto = trainer.profile_photo || trainer.profilePhoto;
    const photoUrl = hasPhoto 
      ? (trainer.profile_photo?.startsWith('/api') ? `${BACKEND_URL}${trainer.profile_photo}` : (trainer.profile_photo || trainer.profilePhoto))
      : null;
    const initial = trainer.name?.charAt(0)?.toUpperCase() || '?';
    
    const coords = trainer.location?.coordinates;
    const trainerLat = Array.isArray(coords) ? coords[1] : (coords?.lat || trainer.location?.lat);
    const trainerLng = Array.isArray(coords) ? coords[0] : (coords?.lng || trainer.location?.lng);
    const navigationUrl = trainerLat && trainerLng 
      ? `https://www.google.com/maps/dir/?api=1&destination=${trainerLat},${trainerLng}`
      : null;
    
    const content = `
      <div style="max-width: 280px; padding: 8px; font-family: system-ui, -apple-system, sans-serif;">
        <div style="display: flex; gap: 10px; align-items: start;">
          ${photoUrl 
            ? `<img src="${photoUrl}" style="width: 50px; height: 50px; border-radius: 8px; object-fit: cover;" onerror="this.style.display='none'" />`
            : `<div style="width: 50px; height: 50px; border-radius: 8px; background: linear-gradient(135deg, #dc2626, #991b1b); display: flex; align-items: center; justify-content: center;">
                 <span style="color: white; font-size: 20px; font-weight: bold;">${initial}</span>
               </div>`
          }
          <div style="flex: 1;">
            <h3 style="margin: 0 0 2px 0; font-size: 14px; font-weight: 600;">${trainer.name}</h3>
            <p style="margin: 0 0 2px 0; font-size: 11px; color: #666;">${trainer.location?.city || ''}</p>
            ${trainer.distanceKm ? `<p style="margin: 0; font-size: 12px; color: #dc2626; font-weight: 600;">${trainer.distanceKm} km</p>` : ''}
          </div>
        </div>
        <div style="display: flex; gap: 8px; margin-top: 10px;">
          <a href="/trainer/${trainer.id}" style="flex: 1; display: block; padding: 8px; background: #dc2626; color: white; text-align: center; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;">View Profile</a>
          ${navigationUrl ? `<a href="${navigationUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; padding: 8px 12px; background: #3b82f6; color: white; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;" title="Get Directions">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>
          </a>` : ''}
        </div>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapRef.current, marker);
  };

  // Helper function to show multiple trainers info window
  const showMultiTrainerInfoWindow = (trainers, marker, lat, lng) => {
    if (!infoWindowRef.current || !mapRef.current) return;
    
    const navigationUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    
    const trainersListHtml = trainers.map(trainer => {
      const hasPhoto = trainer.profile_photo || trainer.profilePhoto;
      const photoUrl = hasPhoto 
        ? (trainer.profile_photo?.startsWith('/api') ? `${BACKEND_URL}${trainer.profile_photo}` : (trainer.profile_photo || trainer.profilePhoto))
        : null;
      const initial = trainer.name?.charAt(0)?.toUpperCase() || '?';
      
      return `
        <a href="/trainer/${trainer.id}" style="display: flex; gap: 10px; align-items: center; padding: 8px; border-radius: 8px; text-decoration: none; color: inherit; transition: background 0.2s;" onmouseover="this.style.background='#f3f4f6'" onmouseout="this.style.background='transparent'">
          ${photoUrl 
            ? `<img src="${photoUrl}" style="width: 40px; height: 40px; border-radius: 8px; object-fit: cover;" onerror="this.style.display='none'" />`
            : `<div style="width: 40px; height: 40px; border-radius: 8px; background: linear-gradient(135deg, #dc2626, #991b1b); display: flex; align-items: center; justify-content: center;">
                 <span style="color: white; font-size: 16px; font-weight: bold;">${initial}</span>
               </div>`
          }
          <div style="flex: 1;">
            <p style="margin: 0; font-size: 13px; font-weight: 600; color: #1f2937;">${trainer.name}</p>
            <p style="margin: 0; font-size: 11px; color: #6b7280;">${trainer.location?.city || ''}</p>
          </div>
          ${trainer.distanceKm ? `<span style="font-size: 11px; color: #dc2626; font-weight: 600;">${trainer.distanceKm} km</span>` : ''}
        </a>
      `;
    }).join('');
    
    const content = `
      <div style="max-width: 300px; font-family: system-ui, -apple-system, sans-serif;">
        <div style="padding: 12px 12px 8px 12px; border-bottom: 1px solid #e5e7eb;">
          <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #1f2937;">
            <span style="display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: #1d4ed8; color: white; border-radius: 50%; font-size: 12px; margin-right: 8px;">${trainers.length}</span>
            Trainers at this location
          </h3>
        </div>
        <div style="max-height: 200px; overflow-y: auto; padding: 4px;">
          ${trainersListHtml}
        </div>
        <div style="padding: 8px 12px; border-top: 1px solid #e5e7eb;">
          <a href="${navigationUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px; background: #3b82f6; color: white; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>
            Get Directions
          </a>
        </div>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapRef.current, marker);
  };


  const handleTrainerSelect = (trainer) => {
    setSelectedTrainer(trainer);
    
    const markerData = markersRef.current.find(m => m.trainerId === trainer.id);
    
    if (mapRef.current && trainer.location?.coordinates) {
      // Handle both array [lng, lat] and object {lat, lng} formats
      const coords = trainer.location.coordinates;
      let lat, lng;
      
      if (Array.isArray(coords)) {
        lng = coords[0];
        lat = coords[1];
      } else {
        lat = coords.lat || trainer.location.lat;
        lng = coords.lng || trainer.location.lng;
      }
      
      if (!lat || !lng) {
        console.warn('Invalid coordinates for trainer selection');
        return;
      }
      
      // Prevent idle event from triggering during pan
      isSelectingTrainerRef.current = true;
      
      // Pan to trainer location
      mapRef.current.panTo({ lat: parseFloat(lat), lng: parseFloat(lng) });
      
      if (isMobile) {
        setSheetHeight(SHEET_HALF);
      }
      
      // Show info window after pan completes and re-enable idle event
      if (markerData) {
        setTimeout(() => {
          showInfoWindow(markerData.marker, trainer);
          // Re-enable idle event after animation completes
          setTimeout(() => {
            isSelectingTrainerRef.current = false;
          }, 500);
        }, 300);
      } else {
        setTimeout(() => {
          isSelectingTrainerRef.current = false;
        }, 500);
      }
    } else if (markerData && mapRef.current) {
      // If no coordinates, just show info window
      showInfoWindow(markerData.marker, trainer);
    }
  };

  const showInfoWindow = (marker, trainer) => {
    const hasPhoto = trainer.profile_photo || trainer.profilePhoto;
    const photoUrl = hasPhoto 
      ? (trainer.profile_photo?.startsWith('/api') ? `${BACKEND_URL}${trainer.profile_photo}` : (trainer.profile_photo || trainer.profilePhoto))
      : null;
    const initial = trainer.name?.charAt(0)?.toUpperCase() || '?';
    
    // Get coordinates for navigation
    const coords = trainer.location?.coordinates;
    const lat = coords?.lat || coords?.[1] || trainer.location?.lat;
    const lng = coords?.lng || coords?.[0] || trainer.location?.lng;
    const navigationUrl = lat && lng 
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : null;
    
    const content = `
      <div style="max-width: 280px; padding: 8px; font-family: system-ui, -apple-system, sans-serif;">
        <div style="display: flex; gap: 10px; align-items: start;">
          ${photoUrl 
            ? `<img src="${photoUrl}" style="width: 50px; height: 50px; border-radius: 8px; object-fit: cover;" />`
            : `<div style="width: 50px; height: 50px; border-radius: 8px; background: linear-gradient(135deg, #dc2626, #991b1b); display: flex; align-items: center; justify-content: center;">
                 <span style="color: white; font-size: 20px; font-weight: bold;">${initial}</span>
               </div>`
          }
          <div style="flex: 1;">
            <h3 style="margin: 0 0 2px 0; font-size: 14px; font-weight: 600;">${trainer.name}</h3>
            <p style="margin: 0 0 2px 0; font-size: 11px; color: #666;">${trainer.location?.city || ''}</p>
            ${trainer.distanceKm ? `<p style="margin: 0; font-size: 12px; color: #dc2626; font-weight: 600;">${trainer.distanceKm} km</p>` : ''}
          </div>
        </div>
        <div style="display: flex; gap: 8px; margin-top: 10px;">
          <a href="/trainer/${trainer.id}" style="flex: 1; display: block; padding: 8px; background: #dc2626; color: white; text-align: center; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;">View Profile</a>
          ${navigationUrl ? `<a href="${navigationUrl}" target="_blank" rel="noopener noreferrer" style="display: flex; align-items: center; justify-content: center; padding: 8px 12px; background: #3b82f6; color: white; border-radius: 6px; text-decoration: none; font-size: 12px; font-weight: 500;" title="Get Directions">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"></polygon></svg>
          </a>` : ''}
        </div>
      </div>
    `;

    infoWindowRef.current.setContent(content);
    infoWindowRef.current.open(mapRef.current, marker);
  };

  const handleAddressSelect = async (address) => {
    if (!address) return;
    
    setSearchedAddress(address);
    setCenterLat(address.coordinates.lat);
    setCenterLng(address.coordinates.lng);
    setMapBrowseMode(false);
    setShowSearchAreaButton(false);
    
    searchNearby(address.coordinates.lat, address.coordinates.lng, radiusKm);
    
    if (isMobile) {
      setViewMode('map');
      setSheetHeight(SHEET_HALF);
    }
  };

  const searchNearby = async (lat, lng, radius) => {
    setLoading(true);
    setHasSearched(true);
    setShowSearchAreaButton(false);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/geo/nearby-trainers`, {
        params: { lat, lng, radiusKm: radius }
      });
      
      setTrainers(response.data.trainers || []);
      setSelectedTrainer(null);
      
      // Update map if it exists (regardless of viewMode)
      if (mapRef.current) {
        mapRef.current.setCenter({ lat, lng });
        mapRef.current.setZoom(getZoomForRadius(radius));
        updateCircle(true);
        updateMarkers();
      }
    } catch (error) {
      console.error('Error searching nearby:', error);
      setTrainers([]);
      toast.error('Search failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const searchInBounds = async () => {
    if (!mapRef.current) return;
    
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const center = mapRef.current.getCenter();

    setLoading(true);
    setShowSearchAreaButton(false);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/geo/trainers-in-bounds`, {
        params: {
          ne_lat: ne.lat(),
          ne_lng: ne.lng(),
          sw_lat: sw.lat(),
          sw_lng: sw.lng(),
          center_lat: center.lat(),
          center_lng: center.lng()
        }
      });
      
      setTrainers(response.data.trainers || []);
      setHasSearched(true);
    } catch (error) {
      console.error('Error searching in bounds:', error);
      toast.error('Search failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchThisArea = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      setCenterLat(center.lat());
      setCenterLng(center.lng());
      searchNearby(center.lat(), center.lng(), radiusKm);
    }
  };

  const handleRadiusChange = (newRadius) => {
    setRadiusKm(newRadius);
    setShowRadiusDropdown(false);
    
    // Always search with new radius (even if not explicitly searched before)
    if (!mapBrowseMode) {
      searchNearby(centerLat, centerLng, newRadius);
    }
  };

  const handleViewOnMap = () => {
    setViewMode('map');
    if (isMobile) {
      setSheetHeight(SHEET_HALF);
    }
  };

  const handleBrowseMap = () => {
    setMapBrowseMode(true);
    setViewMode('map');
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }
    // Don't call searchInBounds immediately - let the 'idle' event handler do it
    // This prevents race conditions where bounds might not be stable yet
    if (isMobile) {
      setSheetHeight(SHEET_COLLAPSED);
    }
  };

  const handleExitBrowseMode = () => {
    setMapBrowseMode(false);
    if (searchedAddress) {
      searchNearby(centerLat, centerLng, radiusKm);
    }
  };

  // Bottom sheet touch handlers
  const handleTouchStart = (e) => {
    if (e.target.closest('.trainer-list-scroll')) return;
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setStartHeight(sheetHeight);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const diff = startY - currentY;
    const diffVh = (diff / window.innerHeight) * 100;
    const newHeight = Math.min(Math.max(startHeight + diffVh, SHEET_COLLAPSED), SHEET_FULL);
    
    setSheetHeight(newHeight);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Snap to nearest state
    if (sheetHeight < (SHEET_COLLAPSED + SHEET_HALF) / 2) {
      setSheetHeight(SHEET_COLLAPSED);
    } else if (sheetHeight < (SHEET_HALF + SHEET_FULL) / 2) {
      setSheetHeight(SHEET_HALF);
    } else {
      setSheetHeight(SHEET_FULL);
    }
  };

  // Trainer card component
  const TrainerCard = ({ trainer, compact = false }) => {
    // Get photo URL - handle both profile_photo and profilePhoto fields
    const photoUrl = trainer.profile_photo || trainer.profilePhoto;
    const fullPhotoUrl = photoUrl?.startsWith('/api') ? `${BACKEND_URL}${photoUrl}` : photoUrl;
    
    return (
    <div
      id={`trainer-${trainer.id}`}
      className={`p-3 rounded-xl cursor-pointer transition-all ${
        selectedTrainer?.id === trainer.id 
          ? 'bg-red-50 dark:bg-red-900/30 ring-2 ring-red-500' 
          : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
      } ${compact ? 'shadow-sm' : 'shadow-md'}`}
      onClick={() => handleTrainerSelect(trainer)}
    >
      <div className="flex gap-3">
        {fullPhotoUrl ? (
          <img
            src={fullPhotoUrl}
            alt={trainer.name}
            className={`${compact ? 'w-14 h-14' : 'w-16 h-16'} rounded-lg object-cover flex-shrink-0`}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        ) : null}
        <div className={`${compact ? 'w-14 h-14' : 'w-16 h-16'} rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex-shrink-0 flex items-center justify-center ${fullPhotoUrl ? 'hidden' : ''}`}>
          <span className="text-white text-xl font-bold">{trainer.name?.charAt(0)?.toUpperCase() || '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-800 dark:text-white text-sm">{trainer.name}</h3>
              <div className="flex items-center gap-1 mt-0.5">
                <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {trainer.reviewCount > 0 ? `${trainer.rating?.toFixed(1)} (${trainer.reviewCount})` : 'New'}
                </span>
              </div>
            </div>
            {trainer.distanceKm && (
              <Badge className="bg-red-100 text-red-700 text-xs px-2 py-0.5">{trainer.distanceKm} km</Badge>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {trainer.location?.city}
          </p>
          {/* Training Types */}
          {trainer.training_types && trainer.training_types.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {trainer.training_types.map((type, idx) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-[10px] font-medium"
                >
                  {type === 'Online' && '🌐'}
                  {type === 'In Person' && '🏋️'}
                  {type === 'Both' && '🌐🏋️'}
                  <span>{type}</span>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="text-red-600 font-bold text-sm">${trainer.pricePerHour || 75}/hr</span>
            <div className="flex items-center gap-2">
              {/* Navigation button */}
              {trainer.location?.coordinates && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${
                    trainer.location.coordinates.lat || trainer.location.coordinates[1]
                  },${
                    trainer.location.coordinates.lng || trainer.location.coordinates[0]
                  }`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center justify-center w-7 h-7 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                  title="Get Directions"
                >
                  <Navigation className="w-3.5 h-3.5" />
                </a>
              )}
              <Link to={`/trainer/${trainer.id}`} onClick={(e) => e.stopPropagation()}>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-xs h-7 px-3">View</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  };

  // Mobile Map View with Bottom Sheet
  if (isMobile && viewMode === 'map') {
    return (
      <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900 overflow-hidden">
        <ResponsiveHeader />
        
        {/* Map Container - Fixed at top */}
        <div className="flex-1 relative" style={{ height: `${100 - sheetHeight}vh` }}>
          <div 
            ref={(el) => {
              mapContainerRef.current = el;
              if (el && isScriptLoaded && !mapRef.current) {
                // Initialize map when container is mounted
                setTimeout(() => initializeMap(), 100);
              }
            }} 
            className="absolute inset-0" 
          />
          
          {/* Search Bar Overlay */}
          <div className="absolute top-2 left-2 right-2 z-10">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-2">
              <SmartLocationSearch
                onLocationSelect={handleAddressSelect}
                placeholder="Search location..."
                compact={true}
              />
            </div>
          </div>

          {/* Map Controls */}
          <div className="absolute top-16 left-2 z-10 flex flex-col gap-2">
            {!mapBrowseMode ? (
              <>
                <button
                  onClick={() => setShowRadiusDropdown(!showRadiusDropdown)}
                  className="bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium"
                >
                  {radiusKm} km <ChevronDown className="w-3 h-3 inline ml-1" />
                </button>
                {showRadiusDropdown && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    {RADIUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleRadiusChange(opt.value)}
                        className={`block w-full px-3 py-2 text-sm text-left ${radiusKm === opt.value ? 'bg-red-50 text-red-600' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={handleBrowseMap}
                  className="bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-lg text-sm"
                >
                  <Navigation className="w-4 h-4 inline mr-1" /> Browse
                </button>
              </>
            ) : (
              <>
                <div className="bg-blue-600 text-white px-3 py-1.5 rounded-lg shadow-lg text-xs">
                  Browse Mode
                </div>
                <button
                  onClick={handleExitBrowseMode}
                  className="bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-lg text-sm"
                >
                  <X className="w-4 h-4 inline" /> Exit
                </button>
              </>
            )}
          </div>

          {/* Search This Area Button */}
          {showSearchAreaButton && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10">
              <button
                onClick={handleSearchThisArea}
                className="bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> Search this area
              </button>
            </div>
          )}

          {/* Trainer Count */}
          <div className="absolute top-16 right-2 z-10 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg shadow-lg">
            <span className="font-bold">{trainers.length}</span>
            <span className="text-gray-500 text-sm ml-1">{loading ? '...' : ''}</span>
          </div>

          {/* Back to List Button */}
          <button
            onClick={() => setViewMode('list')}
            className="absolute bottom-4 right-4 z-10 bg-white dark:bg-gray-800 p-3 rounded-full shadow-lg"
          >
            <List className="w-5 h-5" />
          </button>
        </div>

        {/* Bottom Sheet */}
        <div
          ref={bottomSheetRef}
          className="bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl transition-all duration-300 ease-out"
          style={{ 
            height: `${sheetHeight}vh`,
            touchAction: 'none'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2 cursor-grab">
            <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>

          {/* Sheet Header */}
          <div className="px-4 pb-2 border-b dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-800 dark:text-white">
                {loading ? 'Searching...' : `${trainers.length} trainers`}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setSheetHeight(SHEET_COLLAPSED)}
                  className={`p-1.5 rounded ${sheetHeight === SHEET_COLLAPSED ? 'bg-gray-200' : ''}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSheetHeight(SHEET_FULL)}
                  className={`p-1.5 rounded ${sheetHeight === SHEET_FULL ? 'bg-gray-200' : ''}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>
            {searchedAddress && !mapBrowseMode && (
              <p className="text-xs text-gray-500 mt-1">Within {radiusKm} km of {searchedAddress.formatted_address}</p>
            )}
          </div>

          {/* Trainer List */}
          <div
            ref={trainerListRef}
            className="trainer-list-scroll overflow-y-auto px-4 py-3 space-y-3"
            style={{ height: `calc(${sheetHeight}vh - 80px)` }}
          >
            {trainers.length === 0 && !loading ? (
              <div className="text-center py-8">
                <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No trainers found</p>
                <Button onClick={handleBrowseMap} variant="outline" size="sm" className="mt-3">
                  Browse Map
                </Button>
              </div>
            ) : (
              trainers.map(trainer => (
                <TrainerCard key={trainer.id} trainer={trainer} compact={true} />
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop/Tablet View and Mobile List View
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <ResponsiveHeader />

      {/* Search Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b sticky top-[64px] sm:top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col gap-3">
            <div className="w-full">
              <SmartLocationSearch
                onLocationSelect={handleAddressSelect}
                placeholder="Enter city, neighborhood or address..."
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowRadiusDropdown(!showRadiusDropdown)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:border-gray-300 bg-white dark:bg-gray-800"
                >
                  <span className="font-medium text-gray-700 dark:text-gray-300 text-sm sm:text-base">{radiusKm} km</span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
                {showRadiusDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-28 sm:w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-50 overflow-hidden">
                    {RADIUS_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleRadiusChange(option.value)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
                          radiusKm === option.value ? 'bg-red-50 dark:bg-red-900/30 text-red-600' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex border-2 border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 sm:px-4 py-2 flex items-center gap-1.5 text-sm ${
                    viewMode === 'list' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <List className="w-4 h-4" />
                  <span className="hidden xs:inline sm:inline">List</span>
                </button>
                <button
                  onClick={handleViewOnMap}
                  className={`px-3 sm:px-4 py-2 flex items-center gap-1.5 text-sm ${
                    viewMode === 'map' ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <MapIcon className="w-4 h-4" />
                  <span className="hidden xs:inline sm:inline">Map</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        {/* Initial State */}
        {!hasSearched && viewMode === 'list' && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">Find Personal Trainers</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Enter a city, neighborhood or address to find trainers near that location.
              </p>
              <Button onClick={handleBrowseMap} variant="outline" className="gap-2">
                <MapIcon className="w-4 h-4" /> Browse Map
              </Button>
            </div>
          </div>
        )}

        {/* List View Results */}
        {hasSearched && viewMode === 'list' && (
          <div className="flex-1 p-4 max-w-5xl mx-auto w-full">
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-600" />
                  {loading ? 'Searching...' : `${trainers.length} trainer${trainers.length !== 1 ? 's' : ''} found`}
                </h2>
                {searchedAddress && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Within {radiusKm} km of {searchedAddress.formatted_address}
                  </p>
                )}
              </div>
              <Button onClick={handleBrowseMap} variant="outline" size="sm" className="gap-2">
                <MapIcon className="w-4 h-4" /> Browse Map
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : trainers.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No trainers found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Try expanding your search radius</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  {RADIUS_OPTIONS.filter(o => o.value > radiusKm).map(option => (
                    <Button key={option.value} onClick={() => handleRadiusChange(option.value)} variant="outline">
                      Expand to {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {trainers.map(trainer => (
                  <Card key={trainer.id} className="hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <TrainerCard trainer={trainer} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop Map View */}
        <div className={`flex-1 relative ${viewMode !== 'map' || isMobile ? 'hidden' : ''}`}>
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
            {!mapBrowseMode && (
              <div className="relative">
                <button
                  onClick={() => setShowRadiusDropdown(!showRadiusDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 rounded-lg shadow-lg"
                >
                  <span className="font-medium">{radiusKm} km</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showRadiusDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                    {RADIUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleRadiusChange(opt.value)}
                        className={`w-full px-3 py-2 text-left text-sm ${radiusKm === opt.value ? 'bg-red-50 text-red-600' : ''}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!mapBrowseMode ? (
              <Button onClick={handleBrowseMap} variant="outline" className="bg-white shadow-lg">
                <Navigation className="w-4 h-4 mr-2" /> Browse Freely
              </Button>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm">Browse Mode</div>
                <Button onClick={handleExitBrowseMode} variant="outline" className="bg-white shadow-lg">Exit</Button>
              </div>
            )}
          </div>

          {showSearchAreaButton && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
              <button
                onClick={handleSearchThisArea}
                className="bg-white px-4 py-2 rounded-full shadow-lg font-medium flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> Search this area
              </button>
            </div>
          )}

          <div className="absolute top-4 right-4 z-10 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
            <span className="font-bold">{trainers.length}</span> trainers
          </div>

          <div ref={mapContainerRef} className="w-full h-[calc(100vh-180px)]" />

          {trainers.length > 0 && (
            <div className="absolute bottom-4 left-4 w-80 max-h-96 overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg">
              <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 py-2 border-b font-semibold">
                {trainers.length} Trainers
              </div>
              <div className="p-2 space-y-2">
                {trainers.map(trainer => (
                  <div
                    key={trainer.id}
                    id={`trainer-${trainer.id}`}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedTrainer?.id === trainer.id ? 'bg-red-50 ring-2 ring-red-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleTrainerSelect(trainer)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white font-bold">
                        {trainer.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{trainer.name}</p>
                        <p className="text-xs text-gray-500">{trainer.location?.city} • {trainer.distanceKm} km</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {viewMode === 'list' && <Footer />}
    </div>
  );
};

export default SearchPage;
